'use client';

import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import Image from 'next/image';
import Link from 'next/link';

import { Button } from '@/components/ui/button';
import { useTranslation } from '@/lib/i18n/context';
import { cn } from '@/lib/utils';

export default function AnnouncementPage() {
  const { t, isRTL } = useTranslation();

  const authorName = isRTL ? 'محمد' : 'Mo';

  // House style: Arabic-Indic glyphs for numerals in Arabic prose. Value is
  // never changed — only the digit glyphs are mapped.
  const toArabicDigits = (value: string) =>
    value.replace(/[0-9]/g, (d) => '٠١٢٣٤٥٦٧٨٩'[Number(d)]);

  const post = {
    title: t.announcement['Building the Magic Factory'],
    authorName,
    image: '/images/academy/funding-announcement.webp',
    pubDate: new Date('2026-01-01'),
    description:
      t.announcement[
        "We're excited to share that we've raised $5M from YC, Shorooq, and Sadu."
      ],
    authorImage: '/about/image.png',
  };

  const { title, image, pubDate, description, authorImage } = post;

  return (
    <section className={cn('bg-white py-32')}>
      <div className="container">
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-4 text-center">
          <h1 className="max-w-3xl text-pretty font-serif text-[2rem] md:text-4xl xl:text-5xl">
            {title}
          </h1>
          <h3 className="text-muted-foreground max-w-3xl text-lg md:text-xl">
            {description}
          </h3>
          <div className="flex items-center gap-3 text-sm md:text-base">
            <div className="size-8 overflow-hidden rounded-full border">
              <Image
                src={authorImage}
                alt={authorName}
                width={32}
                height={32}
                className="size-full object-cover"
              />
            </div>
            <span>
              <span className="font-semibold">{authorName}</span>
              <span className={isRTL ? 'mr-1' : 'ml-1'}>
                {isRTL ? 'في' : 'on'}{' '}
                {isRTL
                  ? toArabicDigits(format(pubDate, 'MMMM yyyy', { locale: ar }))
                  : format(pubDate, 'MMMM yyyy')}
              </span>
            </span>
          </div>
          <img
            src={image}
            alt={title}
            className="mb-8 mt-4 aspect-video w-full rounded-lg border object-cover"
          />
        </div>
      </div>
      <div className="container">
        <div className="prose dark:prose-invert mx-auto max-w-3xl">
          <p>
            {
              t.announcement[
                "We're excited to share that we've raised $5M from YC, Shorooq, and Sadu."
              ]
            }
          </p>

          <p>
            {t.announcement['This marks our second funding milestone to date.']}
          </p>

          <p>
            {
              t.announcement[
                "Exactly one year ago, we went live. Since then, we've seen real, measurable impact. We've onboarded some of the world's most recognizable brands, including MoneyGram, Mollie, Viva, TicketSwap, and many others."
              ]
            }
          </p>

          <p>
            {
              t.announcement[
                'Our goal from day one was simple but ambitious: empower companies with the best AI customer support agent and engine in the world. From the beginning, we chose to focus on brands with high-complexity, high-stakes support and communication needs - the kinds of environments many other players shy away from. The results speak for themselves. On average, our customers now automate 77% of their customer interactions, one of the highest automation rates in the market.'
              ]
            }
          </p>

          <p>
            {
              t.announcement[
                'Open enables companies to serve their customers across any channel, in any language, at any time. Beyond resolution, we help teams truly understand their customers through insights drawn directly from conversations, and use those insights to build better products and proactively reach out when it matters most.'
              ]
            }
          </p>

          <p>
            {
              t.announcement[
                "We're using this funding to go deeper. To make our AI engine better, faster, and available everywhere. And to move closer to our long-term vision: one super AI engine that powers the entire customer experience."
              ]
            }
          </p>

          <p>
            {
              t.announcement[
                'We envision a future where Open is the only software a team needs to communicate with their customers using AI. Phone, email, SMS, WhatsApp, web chat. Inbound or outbound. Support, sales, operations, or any other customer-facing use case. One system that understands context across channels, acts in real time, and continuously improves - without teams stitching together fragmented tools.'
              ]
            }
          </p>

          <p>
            {
              t.announcement[
                'Today, customer communication is fragmented by necessity. Support agents answer tickets. QA teams review quality. Analysts extract insights. Specialists handle proactive outreach. Everyone is talking to customers, but the technology forces these roles apart.'
              ]
            }
          </p>

          <p>{t.announcement['That constraint is disappearing.']}</p>

          <p>
            {
              t.announcement[
                'AI can now do what once required multiple layers of human expertise: understand context, maintain quality, extract insights, and reach out proactively. Not in theory, but in practice, today.'
              ]
            }
          </p>

          <p>
            {
              t.announcement[
                'Open is what happens when you stop fragmenting customer communication and let it be what it should have been all along: one coherent system that learns, responds, and improves itself.'
              ]
            }
          </p>

          <p>
            {
              t.announcement[
                "The surprising part isn't that AI can do this. It's that it does it with the empathy and nuance people once thought only humans could provide."
              ]
            }
          </p>

          <p>
            {
              t.announcement[
                "We're deeply grateful to our partners and investors for the trust and support, and to our customers for pushing us every day with real, complex problems. Building alongside teams operating at this level is what shapes the product and keeps us honest about what truly matters."
              ]
            }
          </p>

          <p className="font-medium">- {authorName}</p>

          <div className="not-prose mt-16 flex flex-col items-center gap-4 text-center">
            <p className="text-muted-foreground text-lg">
              {t.announcement['See what Open can do for you']}
            </p>
            <Button
              asChild
              size="lg"
              className="h-12 rounded-full bg-black px-8 text-base font-medium text-white transition-all hover:scale-105 hover:bg-neutral-800 hover:shadow-xl hover:shadow-black/10"
            >
              <Link href={isRTL ? '/ar/demo' : '/demo'}>
                {t.announcement['Request a Demo']}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
